import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Contact, DoorOpen, ArrowRight, LogOut } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/dashboard/Header";
import PhotoFallback from "../components/PhotoFallback";
import { fmtCompact, fmtPeriod } from "../lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (url) => (!url ? "" : url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

function occTone(pct) {
  if (pct === null || pct === undefined) return { bar: "#C7C1B8", text: "text-brand-muted" };
  if (pct >= 0.9) return { bar: "#16A34A", text: "text-brand-pos" };
  if (pct >= 0.75) return { bar: "#EAB308", text: "text-amber-600" };
  return { bar: "#DC2626", text: "text-rose-600" };
}

function LocationCard({ loc, canFin, canPipeline, onOpen }) {
  const pct = loc.occupancy_pct;
  const tone = occTone(pct);
  const photo = mediaUrl(loc.photo_url);
  return (
    <button
      onClick={() => onOpen(loc.location)}
      data-testid={`location-card-${loc.location}`}
      className="group text-left bg-brand-surface border border-brand-border rounded-xl overflow-hidden transition-all duration-200 hover:border-brand-primary hover:shadow-lg hover:-translate-y-0.5 flex flex-col"
    >
      <div className="relative h-40 overflow-hidden">
        {photo ? (
          <img src={photo} alt={loc.location} loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <PhotoFallback label="No photo yet" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/90 via-brand-ink/35 to-transparent pointer-events-none" />
        <h3 className="absolute bottom-3 left-4 right-10 font-serif text-xl leading-tight text-white drop-shadow-sm">{loc.location}</h3>
        <ArrowRight className="absolute top-3 right-3 h-4 w-4 text-white/80 group-hover:translate-x-0.5 transition-transform duration-200" />
      </div>

      <div className="p-5 flex flex-col flex-1">
        {canFin && loc.has_financials && (
          <div>
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.12em] text-brand-tertiary">
              <span>Occupancy</span>
              <span className={`font-medium ${tone.text}`} data-testid={`location-occ-${loc.location}`}>
                {pct === null || pct === undefined ? "—" : `${(pct * 100).toFixed(1)}%`}
              </span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-brand-ink/5 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, (pct || 0) * 100)}%`, background: tone.bar }} />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary">Revenue</p>
                <p className="text-brand-ink font-medium mt-0.5">{fmtCompact(loc.total_revenue)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary">Net Income</p>
                <p className={`font-medium mt-0.5 ${loc.net_income < 0 ? "text-rose-600" : "text-brand-ink"}`}>
                  {fmtCompact(loc.net_income)}
                </p>
              </div>
            </div>
          </div>
        )}

        {canFin && !loc.has_financials && (
          <p className="text-xs text-brand-tertiary italic">No P&amp;L for this location (management deal).</p>
        )}

        {canPipeline && (
          <div className="mt-auto pt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-brand-muted border-t border-brand-border/70">
            <span className="inline-flex items-center gap-1.5" title="Open pipeline">
              <TrendingUp className="h-3.5 w-3.5 text-brand-gold" />
              {fmtCompact(loc.open_pipeline)}
            </span>
            <span className="inline-flex items-center gap-1.5" title="Leads">
              <Contact className="h-3.5 w-3.5 text-brand-tertiary" />
              {loc.lead_count} lead{loc.lead_count === 1 ? "" : "s"}
            </span>
            <span className="inline-flex items-center gap-1.5" title="Upcoming day passes">
              <DoorOpen className="h-3.5 w-3.5 text-brand-tertiary" />
              {loc.upcoming_daypasses}
            </span>
            {loc.moveouts_count > 0 && (
              <span className="inline-flex items-center gap-1.5 text-rose-600" title="On notice / at risk"
                data-testid={`location-moveouts-${loc.location}`}>
                <LogOut className="h-3.5 w-3.5" />
                {loc.moveouts_count} on notice
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

export default function Locations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/locations/overview");
      setData(data);
      // Community managers with a single location go straight into it.
      if (user?.role === "community" && data.locations?.length === 1) {
        navigate(`/locations/${encodeURIComponent(data.locations[0].location)}`, { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [user, navigate]);

  useEffect(() => { load(); }, [load]);

  const open = (loc) => navigate(`/locations/${encodeURIComponent(loc)}`);

  return (
    <div className="min-h-screen bg-brand-page">
      <Header />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight" data-testid="locations-title">Locations</h1>
            <p className="text-sm text-brand-muted mt-2">
              {data?.can_financials
                ? "Every building at a glance — occupancy, revenue and pipeline. Click a location for its full dashboard."
                : "Your buildings at a glance. Click a location for its full dashboard."}
              {data?.period && data?.can_financials && (
                <span className="text-brand-tertiary"> · Financials as of {fmtPeriod(data.period)}</span>
              )}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-52 rounded-xl bg-brand-surface border border-brand-border animate-pulse" />
            ))}
          </div>
        ) : !data?.locations?.length ? (
          <div className="text-center py-24 text-brand-muted" data-testid="locations-empty">
            No locations assigned to your account yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="locations-grid">
            {data.locations.map((loc) => (
              <LocationCard key={loc.location} loc={loc} canFin={data.can_financials}
                canPipeline={data.can_pipeline} onOpen={open} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
